import React from "react";
import { Button } from "@/components";
import { Edit, Trash2, Eye } from "lucide-react";
import type { Brand } from "../interfaces";

interface BrandCardProps {
  brand: Brand;
  onEdit: (brand: Brand) => void;
  onDelete: (brandId: number) => void;
  onView?: (brand: Brand) => void;
}

export const BrandCard: React.FC<BrandCardProps> = ({
  brand,
  onEdit,
  onDelete,
  onView,
}) => {
  return (
    <div className="border border-input rounded-lg p-4 sm:p-6 bg-card hover:shadow-md transition-shadow cursor-pointer" onClick={() => onView?.(brand)}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-semibold mb-2">{brand.title}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {brand.description}
          </p>
        </div>
        <div className="flex gap-2 sm:ml-4 shrink-0 justify-end sm:justify-start" onClick={(e) => e.stopPropagation()}>
          {onView && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onView(brand)}
              title="View brand details"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(brand)}
            title="Edit brand"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(brand.id)}
            title="Delete brand"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {brand.images && brand.images.length > 0 && (
        <div className="mb-4">
          <div className="grid grid-cols-3 gap-2">
            {brand.images.slice(0, 3).map((image, index) => (
              <div
                key={index}
                className="aspect-video rounded-md overflow-hidden bg-muted"
              >
                <img
                  src={image}
                  alt={`${brand.title} image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
          {brand.images.length > 3 && (
            <p className="text-xs text-muted-foreground mt-2">
              +{brand.images.length - 3} more images
            </p>
          )}
        </div>
      )}

      {brand.videos && brand.videos.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-muted-foreground mb-2">
            {brand.videos.length} video{brand.videos.length > 1 ? "s" : ""}
          </p>
        </div>
      )}

      {brand.config?.product_or_service_info && brand.config.product_or_service_info.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium mb-1">Brand Info:</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            {brand.config.product_or_service_info.slice(0, 3).map((info, index) => (
              <li key={index} className="line-clamp-1">
                • {info}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-input">
        <span>
          Created: {new Date(brand.createdAt).toLocaleDateString()}
        </span>
        {brand.strategyIds && brand.strategyIds.length > 0 && (
          <span className="px-2 py-1 bg-primary/10 text-primary rounded">
            {brand.strategyIds.length} plan{brand.strategyIds.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </div>
  );
};
